import { formatMoney } from '../../utils/money';

/**
 * Get the effective price for a variant, falling back to the product base price.
 * @param {object|null} variant - The variant object (may have .price)
 * @param {number|string} basePrice - The product's base price
 * @returns {number}
 */
export const getEffectivePrice = (variant, basePrice) => {
    if (variant?.price !== null && variant?.price !== undefined) {
        return parseFloat(variant.price);
    }
    return parseFloat(basePrice);
};

/**
 * Format a price for display using comma as decimal separator.
 * @param {number} price
 * @returns {string} e.g. "12€", "12,5€"
 */
export const formatPrice = (price) => `${formatMoney(price)}€`;

/**
 * Get the price display string for a product, considering variant-level pricing.
 * Shows a range (e.g. "5€ - 12,5€") if variants have different prices,
 * or a single price if all are the same.
 * @param {object} item - Product with .price and optionally .variants[]
 * @returns {string}
 */
export const getPriceDisplay = (item) => {
    if (item.kind === 'pack') return formatPrice(item.min_price ?? item.price);
    if (!item.variants || item.variants.length === 0) {
        return formatPrice(item.price);
    }

    const prices = item.variants.map(v => getEffectivePrice(v, item.price));
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    if (minPrice === maxPrice) {
        return formatPrice(minPrice);
    }
    return `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;
};

// Preview the same shared inventory used by the server's pack availability.
// Multiple units of one component must all use the same size/color variant.
export const getPackStock = (components, catalog) => {
    if (components.length < 2 || components.some(component => !Number.isInteger(Number(component.quantity)) || Number(component.quantity) < 1 || Number(component.quantity) > 999)) {
        return null;
    }
    return Math.min(...components.map(component => {
        const product = catalog.find(candidate => candidate.id === component.itemId);
        if (!product || Number(product.active) !== 1 || Number(product.deleted) || product.kind === 'pack' || product.type === 'made_to_order') return 0;
        return (product.variants || []).reduce((sum, variant) => sum + Math.floor(Math.max(0, Number(variant.stock) || 0) / Number(component.quantity)), 0);
    }));
};

// Component units appear in picking/sales counts; their revenue belongs to the pack.
export const getSalesLines = items => (items || []).flatMap(item => item.kind === 'pack'
    ? [item, ...(item.components || []).map(component => ({ ...component, quantity: component.quantity * item.quantity, price: 0, includedInPack: true }))]
    : [item]);

// --- Order filters (admin orders list) ---
// Products are keyed by id; old orders without one fall back to their name.
const lineProductKey = line => line.itemId ? `id:${line.itemId}` : `name:${line.name || ''}`;
const lineVariantKey = line => [line.variant?.size || '', line.variant?.color || ''].join('|');
const normalizeText = text => String(text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const variantLabel = key => key.split('|').filter(Boolean).join(' · ') || 'Sense variant';

// Every product and variant that appears in an order, including pack components.
export const getOrderItemOptions = orders => {
    const products = new Map();
    for (const order of orders || []) {
        for (const line of getSalesLines(order.items)) {
            const key = lineProductKey(line);
            const product = products.get(key) || { key, name: line.name || 'Article', variants: new Set() };
            product.variants.add(lineVariantKey(line));
            products.set(key, product);
        }
    }
    return [...products.values()]
        .map(product => ({ ...product, variants: [...product.variants].sort((a, b) => variantLabel(a).localeCompare(variantLabel(b), 'ca')) }))
        .sort((a, b) => a.name.localeCompare(b.name, 'ca'));
};

const matchingLines = (order, { itemKey, variantKey }) => getSalesLines(order.items)
    .filter(line => lineProductKey(line) === itemKey && (!variantKey || lineVariantKey(line) === variantKey));

export const filterOrders = (orders, { statuses = [], itemKey = '', variantKey = '', search = '' } = {}, getName = () => '') => {
    const query = normalizeText(search.trim());
    return (orders || []).filter(order =>
        (statuses.length === 0 || statuses.includes(order.status))
        && (!itemKey || matchingLines(order, { itemKey, variantKey }).length > 0)
        && (!query || normalizeText(getName(order)).includes(query)));
};

// Orders that contain each product (or, with itemKey, each variant of that product).
export const countOrdersByItem = (orders, itemKey = '') => {
    const counts = new Map();
    for (const order of orders || []) {
        const keys = new Set(getSalesLines(order.items)
            .filter(line => !itemKey || lineProductKey(line) === itemKey)
            .map(line => itemKey ? lineVariantKey(line) : lineProductKey(line)));
        keys.forEach(key => counts.set(key, (counts.get(key) || 0) + 1));
    }
    return counts;
};
